import { CANONICAL, GIFI } from "../../../../../shared/helpers/constants";

// DEBT_01..DEBT_03 + TAX_01..TAX_03 — long-term debt, accrued interest,
// income tax provision, and tax payable/expense mapping.

export const debtTaxCascades = {
	// TAX_02 — tax expense without tax payable
	"tax02_determine_reason": {
		type: "labelResponse",
		labels: {
			minimum_tax: "Minimum tax",
			prior_year_adj: "Prior-year adjustment",
			instalment: "Instalment classified as expense",
			error: "Error — should be reversed",
		},
	},
	"tax02_resolve": {
		type: "branch", of: "tax02_determine_reason",
		when: {
			"minimum_tax": { type: "acknowledge", detail: "Acknowledged — minimum tax" },
			"prior_year_adj": { type: "acknowledge", detail: "Acknowledged — prior-year adjustment" },
			"instalment": { type: "acknowledge", detail: "Acknowledged — instalment" },
			"error": {
				type: "pickAccount",
				keywords: ["prepaid", "receivable", "tax refund", "instalment", "deposit"],
				target: { canonicalCode: CANONICAL.TAX_INSTALMENTS, canonicalName: "Taxes recoverable/refundable" },
				question: "Where should the tax expense be reclassified?",
				blockedDetail: "No accounts available",
			},
		},
	},

	// TAX_01 — net income but no tax expense
	"tax01_determine_reason": {
		type: "labelResponse",
		labels: {
			loss_carryforward: "Prior-year losses offset income",
			need_provision: "Tax provision needed",
			tax_exempt: "Entity is tax-exempt",
		},
	},
	"tax01_resolve": {
		type: "branch", of: "tax01_determine_reason",
		when: {
			"loss_carryforward": { type: "acknowledge", detail: "Acknowledged — losses offset income" },
			"tax_exempt": { type: "acknowledge", detail: "Acknowledged — tax-exempt" },
			"need_provision": {
				type: "amountThenAje",
				amountKey: "tax01_resolve_tax_amount",
				promptDetail: "Enter the estimated tax provision",
				promptQuestion: "Estimated income tax provision?",
				inputField: { id: "tax_amount", label: "Tax amount ($)", input_type: "number" },
				proposal: {
					debitCanonicalCode: CANONICAL.INCOME_TAX_EXPENSE, debitDescription: "Current income taxes", debitGifi: GIFI.INCOME_TAX_EXPENSE,
					creditCanonicalCode: CANONICAL.INCOME_TAX_PAYABLE, creditDescription: "Taxes payable", creditGifi: GIFI.INCOME_TAX_PAYABLE,
					description: "Record income tax provision",
				},
			},
		},
	},

	// TAX_03 — tax payable without matching tax expense
	"tax03_determine_reason": {
		type: "labelResponse",
		labels: {
			prior_year: "Prior-year tax balance",
			tax_unmapped: "Tax expense mapped to wrong account",
			instalments: "Tax instalments offset the payable",
			net_loss: "Net loss — payable is prior-year",
		},
	},
	"tax03_resolve": {
		type: "branch", of: "tax03_determine_reason",
		when: {
			"prior_year": { type: "acknowledge", detail: "Acknowledged — prior-year tax balance" },
			"instalments": { type: "acknowledge", detail: "Acknowledged — tax instalments offset the payable" },
			"tax_unmapped": {
				type: "pickAccount",
				keywords: ["tax", "income tax", "provision", "tax expense"],
				target: { canonicalCode: CANONICAL.INCOME_TAX_EXPENSE, canonicalName: "Current income taxes" },
				question: "Which account should be mapped to Income Tax Expense?",
				foundNoun: "account(s) that may be tax expense",
			},
		},
	},

	// DEBT_03 — current portion of LTD missing
	"debt03_determine_reason": {
		type: "labelResponse",
		labels: {
			final_year: "Final year of loan",
			fully_repaid: "Debt fully repaid",
			ltd_unmapped: "LTD exists but is unmapped",
		},
	},
	"debt03_resolve": {
		type: "branch", of: "debt03_determine_reason",
		when: {
			"final_year": { type: "acknowledge", detail: "Acknowledged — final year, entire balance is current" },
			"fully_repaid": { type: "acknowledge", detail: "Acknowledged — debt fully repaid" },
			"ltd_unmapped": {
				type: "pickAccount",
				keywords: ["loan", "mortgage", "long-term", "note payable", "debt"],
				target: { canonicalCode: CANONICAL.BANK_LOAN, canonicalName: "Long-term debt" },
				question: "Which account should be mapped as long-term debt?",
				blockedDetail: "No accounts available",
			},
		},
	},

	// DEBT_02 — interest expense without debt
	"debt02_determine_source": {
		type: "labelResponse",
		labels: {
			credit_facility: "Credit facility — repaid by year-end",
			bank_charges: "Bank charges — should be reclassified",
			debt_unmapped: "Debt exists but is unmapped",
		},
	},
	"debt02_resolve": {
		type: "branch", of: "debt02_determine_source",
		when: {
			"credit_facility": { type: "acknowledge", detail: "Acknowledged — interest from credit facility repaid by year-end" },
			"bank_charges": {
				type: "pickAccount",
				keywords: ["bank", "charges", "fees", "service charge"],
				target: { canonicalCode: CANONICAL.BANK_CHARGES, canonicalName: "Interest and bank charges" },
				question: "Which account should this be reclassified to?",
			},
			"debt_unmapped": {
				type: "pickAccount",
				keywords: ["loan", "mortgage", "credit", "line of credit", "debt", "note payable"],
				target: { canonicalCode: CANONICAL.BANK_LOAN, canonicalName: "Long-term debt" },
				question: "Which account should be mapped as debt?",
			},
		},
	},

	// DEBT_01 — debt without interest expense
	"debt01_determine_reason": {
		type: "labelResponse",
		labels: {
			interest_unmapped: "Interest expense mapped to wrong account",
			need_aje: "Accrued interest needs recording",
			non_interest_bearing: "Debt is non-interest-bearing",
		},
	},
	"debt01_resolve": {
		type: "branch", of: "debt01_determine_reason",
		when: {
			"non_interest_bearing": { type: "acknowledge", detail: "Acknowledged — debt is non-interest-bearing, no interest expected" },
			"interest_unmapped": {
				type: "pickAccount",
				keywords: ["interest", "bank charges", "finance cost", "loan cost"],
				target: { canonicalCode: CANONICAL.INTEREST_EXPENSE, canonicalName: "Interest and bank charges" },
				question: "Which account should be mapped to Interest Expense?",
				foundNoun: "account(s) that may be interest expense",
			},
			"need_aje": {
				type: "amountThenAje",
				amountKey: "debt01_resolve_interest_amount",
				promptDetail: "Enter the accrued interest amount",
				promptQuestion: "What is the accrued interest expense at year-end?",
				inputField: { id: "interest_amount", label: "Interest amount ($)", input_type: "number" },
				proposal: {
					debitCanonicalCode: CANONICAL.INTEREST_EXPENSE,
					debitDescription: "Interest and bank charges",
					debitGifi: GIFI.INTEREST_EXPENSE,
					creditCanonicalCode: CANONICAL.ACCRUED_INTEREST,
					creditDescription: "Interest payable",
					creditGifi: GIFI.ACCRUED_INTEREST,
					description: "Record accrued interest expense at year-end",
				},
			},
		},
	},
};
